import type { CSSProperties } from 'react';
import {
  StageDeploymentIcon,
  StageFinalDispositionIcon,
  StageInitialDispositionIcon,
  StageIntegrationGroupIcon,
  StageQaIcon,
  StageRoadmapIcon,
  type SvgIconComponent,
} from '@platform/ui-kit';
import type { AprApplication } from '../../api';

/**
 * How far an application has moved through the APR lifecycle, as one icon per stage.
 *
 * All six always render, in order, so the column reads as a progress strip you can
 * scan straight down a page rather than a row of icons that move about.
 *
 * Every stage is DERIVED from its authoritative source — none is a stored flag, so an
 * icon can never disagree with the underlying record: Q&A and the dispositions from
 * App Rationalization, the integration group, roadmap and approval from Program Planning
 * & Roadmap.
 */

type StageState = { done: boolean; text: string };

interface Stage {
  key: string;
  label: string;
  Icon: SvgIconComponent;
  state: (application: AprApplication) => StageState;
}

const done = (text: string): StageState => ({ done: true, text });
const notYet = (text: string): StageState => ({ done: false, text: `not yet — ${text}` });

/*
 * Every module behind these stages now exists (Program Planning & Roadmap landed 2026-10-19),
 * so each stage reads the field the list query derives for it (`apr/masterData.ts` LIFECYCLE)
 * instead of the old "not migrated yet" placeholder. The last three are Program Planning's.
 */
const STAGES: readonly Stage[] = [
  {
    key: 'qa',
    label: 'Q&A',
    Icon: StageQaIcon,
    state: (a) => {
      const answered = Number(a.qa_answered_count ?? 0);
      const total = Number(a.qa_total_questions ?? 0);
      if (total > 0 && answered >= total) return done(`all ${total} questions answered`);
      return answered > 0
        ? notYet(`${answered} of ${total} questions answered`)
        : notYet('no questions answered');
    },
  },
  {
    key: 'initial',
    label: 'Initial Disposition',
    Icon: StageInitialDispositionIcon,
    state: (a) =>
      a.has_initial_disposition ? done('initial disposition recorded') : notYet('no initial disposition'),
  },
  {
    key: 'final',
    label: 'Final Disposition',
    Icon: StageFinalDispositionIcon,
    state: (a) =>
      a.has_final_disposition ? done('final disposition recorded') : notYet('no final disposition'),
  },
  {
    key: 'integration',
    label: 'Integration group',
    Icon: StageIntegrationGroupIcon,
    state: (a) =>
      a.has_integration_group
        ? done('in an integration group (Program Planning)')
        : notYet('not in an integration group'),
  },
  {
    key: 'roadmap',
    label: 'HL Roadmap',
    Icon: StageRoadmapIcon,
    state: (a) =>
      a.has_roadmap ? done('scheduled on the roadmap (Program Planning)') : notYet('not scheduled'),
  },
  {
    key: 'deployment',
    label: 'In deployment',
    Icon: StageDeploymentIcon,
    state: (a) =>
      a.deployment_status === 'In Development' || a.deployment_status === 'Deployed'
        ? done(a.deployment_status.toLowerCase())
        : notYet(a.deployment_status ? a.deployment_status.toLowerCase() : 'not approved for the roadmap'),
  },
];

/**
 * PERFORMANCE — why this renders no MUI wrappers.
 *
 * This component is drawn once per row. With a MUI `Tooltip` per stage that was
 * 6 tooltips and 6 `sx`-styled icons per row: at 579 rows, ~7,500 MUI components with
 * refs, popper machinery and event handlers, re-mounting on every keystroke in the
 * filter. It froze the renderer.
 *
 * A native `title` gives the same hover text at no component cost, and the styles are
 * module-level constants so nothing is allocated per row. Identical appearance.
 */
const STRIP_STYLE: CSSProperties = { display: 'flex', gap: 4, alignItems: 'center' };

/** Hoisted so 3,474 icons share two objects rather than allocating their own. */
const DONE_ICON: CSSProperties = { fontSize: 18, cursor: 'help', color: '#2e7d32' };
const PENDING_ICON: CSSProperties = { fontSize: 18, cursor: 'help', color: 'rgba(0,0,0,0.26)' };

export function LifecycleProgress({ application }: { application: AprApplication }) {
  return (
    <span style={STRIP_STYLE}>
      {STAGES.map(({ key, label, Icon, state }) => {
        const { done, text } = state(application);
        return (
          <Icon
            key={key}
            titleAccess={`${label}: ${text}`}
            style={done ? DONE_ICON : PENDING_ICON}
          />
        );
      })}
    </span>
  );
}

export const LIFECYCLE_STAGE_COUNT = STAGES.length;
